import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import styles from "../../../styles/pages/profile/ProfilePage.module.css";
import {
  FaHistory,
  FaStar,
  FaCommentDots,
  FaList,
  FaHeart,
  FaThumbsUp,
  FaSearch,
  FaBookmark,
  FaQuestionCircle,
  FaComment,
  FaUsers,
  FaUserPlus,
  FaCamera,
  FaExclamationTriangle,
  FaClock,
  FaFilter,
  FaCalendarAlt,
  FaShoppingBag,
} from "react-icons/fa";
import { formatRelativeTime, formatDate } from "../../../utils/dateFormatter";
import { renderMentionText } from "../../../utils/renderMentionText";
import ActivityViewSkeleton from "./ActivityViewSkeleton";

const ITEMS_PER_PAGE = 6;

const MARKETPLACE_ACTIVITY_LABELS = {
  marketplace_listing_view: "Viewed listing",
  marketplace_listing_save: "Saved listing",
  marketplace_listing_unsave: "Removed listing from saved",
  marketplace_listing_sold: "Marked listing as sold",
  marketplace_review_created: "Reviewed listing",
  marketplace_offer_created: "Made an offer on listing",
  marketplace_offer_accepted: "Accepted an offer on listing",
  marketplace_offer_rejected: "Rejected an offer on listing",
  marketplace_offer_withdrawn: "Withdrew an offer on listing",
  marketplace_saved_search_created: "Saved a marketplace search",
  marketplace_saved_search_deleted: "Removed a saved search",
  marketplace_filter_applied: "Applied marketplace filters",
  marketplace_near_me_used: "Used near-me search",
  marketplace_view_mode_changed: "Changed marketplace view",
  marketplace_map_viewed: "Viewed marketplace map",
  marketplace_widget_impression: "Saw marketplace suggestions",
  marketplace_widget_click: "Clicked marketplace suggestions",
};

const formatUnknownActivityType = (type) => {
  if (!type) return "Activity";
  return type
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const MARKETPLACE_ANALYTICS_TYPES = new Set([
  "marketplace_filter_applied",
  "marketplace_near_me_used",
  "marketplace_view_mode_changed",
  "marketplace_map_viewed",
  "marketplace_widget_impression",
  "marketplace_widget_click",
  "marketplace_saved_search_created",
  "marketplace_saved_search_deleted",
]);

const isMarketplaceActivity = (type) =>
  typeof type === "string" && type.startsWith("marketplace_");

const isObjectId = (id) =>
  typeof id === "string" && /^[a-f\d]{24}$/i.test(id);

const ActivityView = ({
  activities,
  loading,
  error,
  onRefresh,
  currentTab,
}) => {
  const [filterType, setFilterType] = useState("all");
  const [dateRange, setDateRange] = useState("all"); // 'all' | 'today' | 'week' | 'month'
  const [currentPage, setCurrentPage] = useState(1);

  // Filter activities
  const filteredActivities = useMemo(() => {
    let result = [...activities];

    console.log("Filtering activities - initial count:", result.length);
    console.log("Filter type:", filterType, "Date range:", dateRange);

    // Filter by type
    if (filterType !== "all") {
      const beforeTypeFilter = result.length;
      result = result.filter((activity) => activity.type === filterType);
      console.log(
        `Type filter "${filterType}": ${beforeTypeFilter} -> ${result.length}`,
      );
    }

    // Filter by date range
    if (dateRange !== "all") {
      const beforeDateFilter = result.length;
      const now = new Date();
      result = result.filter((activity) => {
        const activityDate = new Date(activity.createdAt);
        const diffDays = Math.floor(
          (now - activityDate) / (1000 * 60 * 60 * 24),
        );

        switch (dateRange) {
          case "today":
            return diffDays === 0;
          case "week":
            return diffDays <= 7;
          case "month":
            return diffDays <= 30;
          default:
            return true;
        }
      });
      console.log(
        `Date filter "${dateRange}": ${beforeDateFilter} -> ${result.length}`,
      );
    }

    // Sort by date (newest first)
    result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    console.log("Final filtered count:", result.length);
    return result;
  }, [activities, filterType, dateRange]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, dateRange, activities.length]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredActivities.length / ITEMS_PER_PAGE),
  );
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * ITEMS_PER_PAGE;
  const pageEnd = pageStart + ITEMS_PER_PAGE;
  const paginatedActivities = filteredActivities.slice(pageStart, pageEnd);
  const showingStart = filteredActivities.length === 0 ? 0 : pageStart + 1;
  const showingEnd = Math.min(pageEnd, filteredActivities.length);

  const getActivityIcon = (type) => {
    switch (type) {
      case "review":
        return <FaStar />;
      case "comment":
        return <FaCommentDots />;
      case "list":
        return <FaList />;
      case "favorite":
        return <FaHeart />;
      case "match":
        return <FaThumbsUp />;
      case "search":
        return <FaSearch />;
      case "question":
        return <FaQuestionCircle />;
      case "answer":
        return <FaComment />;
      case "saved":
        return <FaBookmark />;
      case "post":
        return <FaCamera />;
      case "follow":
        return <FaUsers />;
      case "follow-request":
        return <FaUserPlus />;
      default:
        if (isMarketplaceActivity(type)) {
          return <FaShoppingBag />;
        }
        return <FaHistory />;
    }
  };

  const getActivityIconClass = (type) => {
    return `${styles.activityIcon} ${styles[`activityIcon${type.charAt(0).toUpperCase() + type.slice(1)}`]}`;
  };

  const renderMarketplaceListingLink = (activity, fallbackLabel = "a listing") => {
    const rawListingId = activity.listingId || activity.sourceId;
    const listingId = isObjectId(rawListingId) ? rawListingId : null;
    const listingTitle = activity.listingTitle || fallbackLabel;

    if (!listingId) {
      return <span>{listingTitle}</span>;
    }

    return (
      <Link
        to={`/marketplace/${listingId}`}
        state={{ fromProfileTab: currentTab }}
      >
        {listingTitle}
      </Link>
    );
  };

  const getActivityDescription = (activity) => {
    if (isMarketplaceActivity(activity.type)) {
      const label =
        MARKETPLACE_ACTIVITY_LABELS[activity.type] ||
        formatUnknownActivityType(activity.type);
      const showListingLink = !MARKETPLACE_ANALYTICS_TYPES.has(activity.type);

      return (
        <>
          {label}
          {showListingLink ? <> {renderMarketplaceListingLink(activity)}</> : null}
        </>
      );
    }

    switch (activity.type) {
      case "review":
        return (
          <>
            Reviewed{" "}
            <Link
              to={`/products/${activity.product?._id}7reviews`}
              state={{ fromProfileTab: currentTab }}
            >
              {activity.product?.name || "a product"}
            </Link>
            {activity.rating && (
              <span className={styles.activityRating}>
                {" "}
                · {activity.rating} <FaStar className={styles.starIconSmall} />
              </span>
            )}
          </>
        );
      case "comment":
        const commentLink =
          activity.link && activity.link !== "7" ? activity.link : null;
        const captionText = activity.targetTitle || "the post";
        return (
          <>
            Commented on post{" "}
            {commentLink ? (
              <Link to={commentLink} state={{ fromProfileTab: currentTab }}>
                {captionText}
              </Link>
            ) : (
              <span>{captionText}</span>
            )}
          </>
        );
      case "list":
        return <>Created list {activity.list?.name || "Untitled"}</>;
      case "favorite":
        return (
          <>
            Favorited{" "}
            <Link
              to={`/products/${activity.product?._id}`}
              state={{ fromProfileTab: currentTab }}
            >
              {activity.product?.name || "a product"}
            </Link>
          </>
        );
      case "match":
        return (
          <>
            Found matches for <span>{activity.category || "products"}</span>
          </>
        );
      case "search":
        return (
          <>
            Searched for <span>"{activity.query || "products"}"</span>
          </>
        );
      case "question":
        return (
          <>
            Asked question{" "}
            <span>{activity.question?.title || "a question"}</span>
          </>
        );
      case "answer":
        return (
          <>
            Answered question{" "}
            <span>{activity.question?.title || "a question"}</span>
          </>
        );
      case "saved": {
        const savedLink = activity.post?._id
          ? `/post/${activity.post._id}`
          : null;
        const savedCaption = activity.post?.caption || "a post";
        const savedCaptionContent = savedCaption.includes("@[")
          ? renderMentionText(savedCaption)
          : savedLink ? (
              <Link to={savedLink} state={{ fromProfileTab: currentTab }}>
                {savedCaption}
              </Link>
            ) : (
              <span>{savedCaption}</span>
            );
        return <>Saved post {savedCaptionContent}</>;
      }
      case "post": {
        const postLink = activity.post?._id
          ? `/post/${activity.post._id}`
          : null;
        const postCaption = activity.post?.caption || "a post";
        const postCaptionContent = postCaption.includes("@[")
          ? renderMentionText(postCaption)
          : postLink ? (
              <Link to={postLink} state={{ fromProfileTab: currentTab }}>
                {postCaption}
              </Link>
            ) : (
              <span>{postCaption}</span>
            );
        return <>Created post {postCaptionContent}</>;
      }
      case "follow":
        const followedUserName =
          activity.user?.firstName || activity.user?.lastName
            ? `${activity.user?.firstName || ""} ${activity.user?.lastName || ""}`.trim()
            : activity.user?.username || "a user";
        return (
          <>
            Followed{" "}
            {activity.user?._id ? (
              <Link
                to={`/user/${activity.user._id}`}
                state={{ fromProfileTab: currentTab }}
              >
                {followedUserName}
              </Link>
            ) : (
              <span>{followedUserName}</span>
            )}
          </>
        );
      case "follow-request":
        const requestUserName =
          activity.user?.firstName || activity.user?.lastName
            ? `${activity.user?.firstName || ""} ${activity.user?.lastName || ""}`.trim()
            : activity.user?.username || "a user";
        return (
          <>
            Requested to follow{" "}
            {activity.user?._id ? (
              <Link
                to={`/user/${activity.user._id}`}
                state={{ fromProfileTab: currentTab }}
              >
                {requestUserName}
              </Link>
            ) : (
              <span>{requestUserName}</span>
            )}{" "}
            {activity.status && activity.status !== "pending" && (
              <span>({activity.status})</span>
            )}
          </>
        );
      default:
        return activity.description || formatUnknownActivityType(activity.type);
    }
  };

  if (loading) {
    return <ActivityViewSkeleton />;
  }

  if (error) {
    return (
      <div className={styles.contentSection}>
        <div className={styles.errorState}>
          <div className={styles.errorStateIcon}>
            <FaExclamationTriangle size={48} />
          </div>
          <h3>Unable to Load Activity</h3>
          <p>{error}</p>
          <button className="buttonPrimary" onClick={onRefresh}>
            <FaClock /> Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.contentSection}>
      <div className={styles.sectionHeader}>
        <div>
          <h2 className={styles.sectionTitle}>
            <FaHistory className={styles.sectionIcon} />
            Activity History
          </h2>
          {activities.length > 0 && (
            <p className={styles.sectionSubtitle}>
              {filteredActivities.length} activit
              {filteredActivities.length === 1 ? "y" : "ies"}
            </p>
          )}
        </div>
        {activities.length > 0 && (
          <button
            className="buttonSecondary"
            onClick={onRefresh}
            disabled={loading}
            title="Refresh Activity"
          >
            <FaClock /> Refresh
          </button>
        )}
      </div>

      {activities.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>
            <FaHistory size={64} />
          </div>
          <h3>No Activity Yet</h3>
          <p>
            Your activity history will appear here as you interact with the
            platform.
          </p>
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className={styles.filterBar}>
            <div className={styles.filterGroup}>
              <FaFilter className={styles.filterIcon} />
              <label>Type:</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">All Activities</option>
                <option value="review">Reviews</option>
                <option value="comment">Comments</option>
                <option value="list">Lists</option>
                <option value="match">Matches</option>
                <option value="favorite">Favorites</option>
                <option value="search">Searches</option>
                <option value="question">Questions</option>
                <option value="answer">Answers</option>
                <option value="saved">Saved Posts</option>
                <option value="post">Posts</option>
                <option value="follow">Follows</option>
                <option value="follow-request">Follow Requests</option>
              </select>
            </div>

            <div className={styles.filterGroup}>
              <FaCalendarAlt className={styles.filterIcon} />
              <label>Date:</label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
            </div>
          </div>

          {/* Activity Timeline */}
          {filteredActivities.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No activities found for the selected filters.</p>
            </div>
          ) : (
            <>
              <div className={styles.activityTimeline}>
                {paginatedActivities.map((activity, index) => (
                  <div
                    key={
                      activity._id ||
                      `${activity.type}-${activity.sourceId || activity.listingId || index}`
                    }
                    className={styles.activityItem}
                  >
                    <div className={getActivityIconClass(activity.type)}>
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className={styles.activityContent}>
                      <div className={styles.activityDescription}>
                        {getActivityDescription(activity)}
                      </div>
                      {(activity.type === "comment" ||
                        activity.type === "answer" ||
                        activity.type === "review") &&
                        (
                          activity.content ||
                          activity.comment ||
                          activity.text ||
                          ""
                        ).trim() && (
                          <div className={styles.activityCommentText}>
                            {renderMentionText(
                              (
                                activity.content ||
                                activity.comment ||
                                activity.text ||
                                ""
                              ).trim(),
                            )}
                          </div>
                        )}
                      <div className={styles.activityMeta}>
                        <span
                          className={styles.activityTime}
                          title={formatDate(activity.createdAt)}
                        >
                          {formatRelativeTime(activity.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filteredActivities.length > ITEMS_PER_PAGE && (
                <div className={styles.paginationBar}>
                  <button
                    className={`buttonSecondary ${styles.paginationButton}`}
                    onClick={() =>
                      setCurrentPage((page) => Math.max(1, page - 1))
                    }
                    disabled={safePage === 1}
                  >
                    Previous
                  </button>
                  <span className={styles.paginationInfo}>
                    {showingStart}-{showingEnd} of {filteredActivities.length}
                  </span>
                  <button
                    className={`buttonSecondary ${styles.paginationButton}`}
                    onClick={() =>
                      setCurrentPage((page) => Math.min(totalPages, page + 1))
                    }
                    disabled={safePage === totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default React.memo(ActivityView);
